import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const CONTACT_API_URL = process.env.REACT_APP_CONTACT_API_URL || 'https://rjd-tech-server.vercel.app';
const CONTACT_EMAIL_TO = process.env.REACT_APP_CONTACT_EMAIL_TO || 'info@alwaysinventingmore.com';

// Debug logging
console.log('Environment variables:', {
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  NODE_ENV: process.env.NODE_ENV,
  API_URL: API_URL
});

// Create axios instance with base URL
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const contactAPI = {
  sendMessage: async (data: { name: string; email: string; subject: string; message: string }) => {
    const response = await axios.post(`${CONTACT_API_URL}/emails/send-email`, { ...data, source: 'aim', to: CONTACT_EMAIL_TO }, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  },
};

// Authentication API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await apiClient.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (name: string, email: string, password: string, role: string, additionalData?: any) => {
    console.log('API: Making registration request to:', `${API_URL}/auth/register`);
    
    const requestData = { name, email, password, role, ...additionalData };
    console.log('API: Request data:', { ...requestData, password: '***' });
    
    const response = await apiClient.post('/auth/register', requestData);
    return response.data;
  },
  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },  
  getProfile: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  getAllUsers: async () => {
    const response = await apiClient.get('/auth/users');
    return response.data;
  },
  updateUser: async (userId: string, userData: { name?: string; email?: string; role?: string }) => {
    const response = await apiClient.put(`/auth/users/${userId}`, userData);
    return response.data;
  },
  deleteUser: async (userId: string) => {
    const response = await apiClient.delete(`/auth/users/${userId}`);
    return response.data;
  }
};

// Vehicle API
export const vehicleAPI = {
  getVehicles: async () => {
    console.log('API: Getting vehicles');
    const response = await apiClient.get('/vehicles');
    
    // Map vehicleModel back to model for frontend consistency
    if (response.data.success && response.data.data) {
      response.data.data = response.data.data.map((vehicle: any) => ({
        ...vehicle,
        model: vehicle.vehicleModel,
      }));
    }
    
    return response.data;
  },
  createVehicle: async (data: any) => {
    console.log('API: Creating vehicle:', data);
    const response = await apiClient.post('/vehicles', data);
    
    // Map vehicleModel back to model for frontend consistency
    if (response.data.success && response.data.data) {
      response.data.data = {
        ...response.data.data,
        model: response.data.data.vehicleModel,
      };
    }
    
    return response.data;
  },
  updateVehicle: async (id: string, data: any) => {
    console.log('API: Updating vehicle:', id, data);
    const response = await apiClient.put(`/vehicles/${id}`, data);
    
    // Map vehicleModel back to model for frontend consistency
    if (response.data.success && response.data.data) {
      response.data.data = {
        ...response.data.data,
        model: response.data.data.vehicleModel,
      };
    }
    
    return response.data;
  },
  deleteVehicle: async (id: string) => {
    console.log('API: Deleting vehicle:', id);
    const response = await apiClient.delete(`/vehicles/${id}`);
    return response.data;
  },
  getVehicle: async (id: string) => {
    console.log('API: Getting vehicle:', id);
    const response = await apiClient.get(`/vehicles/${id}`);
    
    // Map vehicleModel back to model for frontend consistency
    if (response.data.success && response.data.data) {
      response.data.data = {
        ...response.data.data,
        model: response.data.data.vehicleModel,
      };
    }
    
    return response.data;
  },
};

// Maintenance Records API
export const maintenanceAPI = {
  getMaintenanceRecords: async (params?: any) => {
    console.log('API: Getting maintenance records with params:', params);
    const response = await apiClient.get('/maintenance', { params });
    
    // Map vehicleModel back to model for frontend consistency in populated vehicle data
    if (response.data.success && response.data.data) {
      response.data.data = response.data.data.map((record: any) => {
        if (record.vehicleId && record.vehicleId.vehicleModel) {
          record.vehicleId.model = record.vehicleId.vehicleModel;
        }
        return record;
      });
    }
    
    return response.data;
  },
  createMaintenanceRecord: async (data: any) => {
    console.log('API: Creating maintenance record:', data);
    const response = await apiClient.post('/maintenance', data);
    
    // Map vehicleModel back to model for frontend consistency
    if (response.data.success && response.data.data && response.data.data.vehicleId) {
      if (response.data.data.vehicleId.vehicleModel) {
        response.data.data.vehicleId.model = response.data.data.vehicleId.vehicleModel;
      }
    }
    
    return response.data;
  },
  updateMaintenanceRecord: async (id: string, data: any) => {
    console.log('API: Updating maintenance record:', id, data);
    const response = await apiClient.put(`/maintenance/${id}`, data);
    
    // Map vehicleModel back to model for frontend consistency
    if (response.data.success && response.data.data && response.data.data.vehicleId) {
      if (response.data.data.vehicleId.vehicleModel) {
        response.data.data.vehicleId.model = response.data.data.vehicleId.vehicleModel;
      }
    }
    
    return response.data;
  },
  deleteMaintenanceRecord: async (id: string) => {
    console.log('API: Deleting maintenance record:', id);
    const response = await apiClient.delete(`/maintenance/${id}`);
    return response.data;
  },
  getMaintenanceRecord: async (id: string) => {
    console.log('API: Getting maintenance record:', id);
    const response = await apiClient.get(`/maintenance/${id}`);
    
    // Map vehicleModel back to model for frontend consistency
    if (response.data.success && response.data.data && response.data.data.vehicleId) {
      if (response.data.data.vehicleId.vehicleModel) {
        response.data.data.vehicleId.model = response.data.data.vehicleId.vehicleModel;
      }
    }
    
    return response.data;
  },
  getMaintenanceStats: async (params?: any) => {
    console.log('API: Getting maintenance stats with params:', params);
    const response = await apiClient.get('/maintenance/stats', { params });
    return response.data;
  },
};

// Reminders API
export const reminderAPI = {
  getReminders: async (params?: any) => {
    const response = await apiClient.get('/reminders', { params });
    return response.data;
  },
  createReminder: async (data: any) => {
    const response = await apiClient.post('/reminders', data);
    return response.data;
  },
  updateReminder: async (id: string, data: any) => {
    const response = await apiClient.put(`/reminders/${id}`, data);
    return response.data;
  },
  deleteReminder: async (id: string) => {
    const response = await apiClient.delete(`/reminders/${id}`);
    return response.data;
  },
};

// Mechanics API
export const mechanicAPI = {
  getMechanics: async (params?: any) => {
    console.log('API: Getting mechanics with params:', params);
    const response = await apiClient.get('/mechanics', { params });
    return response.data;
  },
  getMechanic: async (id: string) => {
    console.log('API: Getting mechanic:', id);
    const response = await apiClient.get(`/mechanics/${id}`);
    return response.data;
  },
  updateMechanicProfile: async (data: any) => {
    console.log('API: Updating mechanic profile:', data);
    const response = await apiClient.put('/mechanics/profile', data);
    return response.data;
  },
  getMechanicStats: async () => {
    console.log('API: Getting mechanic stats');
    const response = await apiClient.get('/mechanics/stats');
    return response.data;
  },
};

// Reviews API
export const reviewAPI = {
  getReviewsForMechanic: async (mechanicId: string, params?: any) => {
    console.log('API: Getting reviews for mechanic:', mechanicId, 'with params:', params);
    const response = await apiClient.get(`/reviews/mechanic/${mechanicId}`, { params });
    return response.data;
  },
  getReviewsByUser: async (params?: any) => {
    console.log('API: Getting reviews by user with params:', params);
    const response = await apiClient.get('/reviews/user', { params });
    return response.data;
  },
  createReview: async (data: any) => {
    console.log('API: Creating review:', data);
    const response = await apiClient.post('/reviews', data);
    return response.data;
  },
  updateReview: async (id: string, data: any) => {
    console.log('API: Updating review:', id, data);
    const response = await apiClient.put(`/reviews/${id}`, data);
    return response.data;
  },
  deleteReview: async (id: string) => {
    console.log('API: Deleting review:', id);
    const response = await apiClient.delete(`/reviews/${id}`);
    return response.data;
  },
  markReviewHelpful: async (id: string) => {
    console.log('API: Marking review as helpful:', id);
    const response = await apiClient.post(`/reviews/${id}/helpful`);
    return response.data;
  },
};

// Savings Account API
export const savingsAPI = {
  getSavingsAccount: async () => {
    const response = await apiClient.get('/savings');
    return response.data;
  },
  updateSavingsAccount: async (data: any) => {
    const response = await apiClient.put('/savings', data);
    return response.data;
  },
  getTransactions: async (params?: any) => {
    const response = await apiClient.get('/savings/transactions', { params });
    return response.data;
  },
  createTransaction: async (data: any) => {
    const response = await apiClient.post('/savings/transactions', data);
    return response.data;
  },
};

// Maintenance Schedules API
export const maintenanceScheduleAPI = {
  getSchedulesForVehicle: async (vehicleId: string) => {
    console.log('API: Getting maintenance schedules for vehicle:', vehicleId);
    const response = await apiClient.get(`/maintenance-schedules/vehicle/${vehicleId}`);
    return response.data;
  },
  getMaintenancePredictions: async (vehicleId: string) => {
    console.log('API: Getting maintenance predictions for vehicle:', vehicleId);
    const response = await apiClient.get(`/maintenance-schedules/predictions/${vehicleId}`);
    return response.data;
  },
  getAllSchedules: async (params?: any) => {
    console.log('API: Getting all maintenance schedules with params:', params);
    const response = await apiClient.get('/maintenance-schedules', { params });
    return response.data;
  },
  getScheduleMetadata: async () => {
    console.log('API: Getting maintenance schedule metadata');
    const response = await apiClient.get('/maintenance-schedules/metadata');
    return response.data;
  },
  initializeSchedules: async () => {
    console.log('API: Initializing standard maintenance schedules');
    const response = await apiClient.post('/maintenance-schedules/initialize');
    return response.data;
  },
  createSchedule: async (data: any) => {
    console.log('API: Creating custom maintenance schedule:', data);
    const response = await apiClient.post('/maintenance-schedules', data);
    return response.data;
  },
};

export default apiClient; 